import { Box, FormControl, Popover, TextField } from "@mui/material";
import { type DateRange as MuiDateRange, DateRangeCalendar, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { type DateTime } from "luxon";
import { type MouseEvent, useCallback, useEffect, useRef, useState } from "react";

export interface DateRangeValue {
  startDate: DateTime | null;
  endDate: DateTime | null;
}

export interface PopoverDateRangePickerProps {
  label?: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  size?: "small" | "medium";
  fullWidth?: boolean;
  disabled?: boolean;
}

export const PopoverDateRangePicker = ({
  label = "Date Range",
  value = { endDate: null, startDate: null },
  onChange,
  size = "small",
  fullWidth = true,
  disabled = false,
}: PopoverDateRangePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const [localValue, setLocalValue] = useState<DateRangeValue>(value);
  const [tempValue, setTempValue] = useState<DateRangeValue>({ endDate: null, startDate: null });

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!disabled) {
      // Reset both dates so user must select start and end fresh
      setTempValue({
        endDate: null,
        startDate: null,
      });
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateRangeChange = (newValue: MuiDateRange<DateTime>) => {
    const updatedValue = {
      endDate: newValue[1],
      startDate: newValue[0],
    };

    setTempValue(updatedValue);

    if (newValue[0] && newValue[1]) {
      setLocalValue(updatedValue);
      onChange(updatedValue);
      handleClose();
    }
  };

  const formatDateDisplay = useCallback(() => {
    const { startDate, endDate } = localValue;
    if (!startDate && !endDate) {
      return "";
    }

    const startDisplay = startDate ? startDate.toFormat("MMM d, yyyy") : "...";
    const endDisplay = endDate ? endDate.toFormat("MMM d, yyyy") : "...";

    return `${startDisplay} - ${endDisplay}`;
  }, [localValue]);

  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  return (
    <FormControl fullWidth={fullWidth} size={size}>
      <TextField
        aria-describedby={id}
        disabled={disabled}
        fullWidth={fullWidth}
        inputRef={inputRef}
        label={label}
        onClick={handleClick}
        placeholder="Select date range"
        size={size}
        sx={{
          "& .MuiInputBase-root": {
            cursor: disabled ? "not-allowed" : "pointer",
          },
          "& input": {
            cursor: disabled ? "not-allowed" : "pointer",
          },
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        value={formatDateDisplay()}
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
      />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
      >
        <Box sx={{ p: 1 }}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateRangeCalendar
              calendars={2}
              disableFuture
              onChange={handleDateRangeChange}
              value={[tempValue.startDate, tempValue.endDate]}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </FormControl>
  );
};
